/**
 * Authentication service
 * Handles user authentication operations
 */

import { api, buildUrl } from "../utils/api-client";
import { logger } from '@/lib/logger'
import {
  setAuthTokens,
  setUserData,
  setUserRole,
  clearAuthTokens,
  clearUserData,
  getAuthHeader,
  STORAGE_KEYS,
} from "../utils/auth-storage";
import { showSignOutCurtain } from "../utils/sign-out-curtain";
import type {
  LoginRequest,
  SignupRequest,
  AuthResponse,
  RefreshTokenResponse,
} from "../types";

/**
 * Authentication service class
 */
export class AuthService {
  /**
   * Signs in a user with email and password
   * @param credentials - Login credentials
   * @returns Promise resolving to authentication response
   */
  static async signin(credentials: LoginRequest): Promise<AuthResponse> {
    // Check for preferred workspace in localStorage for this specific user
    // This allows the user to sign in to their last selected workspace
    const userWorkspaceKey = `currentWorkspaceId_${credentials.email}`
    const savedWorkspaceId = localStorage.getItem(userWorkspaceKey)

    // Prepare signin request with workspace preference
    // Include saved workspace ID if available
    const signinRequest = {
      ...credentials,
      ...(savedWorkspaceId && { workspaceId: savedWorkspaceId })
    }

    const _response = await api.post("/auth/signin", signinRequest, {
      requiresAuth: false,
      showSuccessToast: false, // We'll handle success in the component
      showErrorToast: false, // We'll handle errors in the component for better UX
    });
    const response = _response.data;

    // Store authentication data if signin successful
    if (response.session?.sessionToken) {
      const tokens = {
        accessToken: response.session.sessionToken,
        refreshToken: response.session.refreshToken || "",
        expiresAt: response.expiresAt || new Date(response.session.expires).getTime(),
        refreshExpiresAt: response.refreshExpiresAt, // Store refresh token expiry from server
      };

      setAuthTokens(tokens);
      setUserData(response.user);

      // Store the selected workspace for future logins for this specific user
      // This ensures user returns to their last selected workspace
      if (response.selectedWorkspace?.id && response.user?.email) {
        const userWorkspaceKey = `currentWorkspaceId_${response.user.email}`
        localStorage.setItem(userWorkspaceKey, response.selectedWorkspace.id)
      }

      // Fetch workspace members to determine the current user's role.
      // The role is stored in the user_workspaces table and returned
      // by the /workspace/members endpoint.
      try {
        const membersResponse = await api.get('/workspace/members', {
          requiresAuth: true,
          showErrorToast: false,
        });
        if (membersResponse.success && Array.isArray(membersResponse.data)) {
          const currentMember = membersResponse.data.find(
            (m: { id?: string; email?: string }) =>
              m.id === response.user?.id || m.email === response.user?.email
          );
          if (currentMember?.role === 'admin') {
            setUserRole('admin');
          } else {
            setUserRole('user');
          }
        }
      } catch {
        // Non-critical — role will fall back to defaults
      }
    } else {
      // A successful sign-in that carries no session must not leave the
      // PREVIOUS account's credentials in storage. Whoever just authenticated
      // is not that user, and anything they do next (creating their first
      // workspace, for one) would be attributed to the stale identity.
      clearAuthTokens();
      clearUserData();
    }

    return response;
  }

  /**
   * Signs up a new user
   * @param userData - User registration data
   * @returns Promise resolving to signup response
   */
  static async signup(userData: SignupRequest): Promise<AuthResponse> {
    try {
      const _response = await api.post("/auth/signup", userData, {
        requiresAuth: false,
        showSuccessToast: false, // We'll handle success in the component
        showErrorToast: true,
      });
      const response = _response.data;


      // Store authentication data if signup successful and user doesn't need verification
      if (response.user && !response.redirectUrl?.includes("/verify-otp")) {
        // Don't store tokens for signup since user needs to verify email first
        // Only store tokens if user is immediately authenticated (not requiring verification)
        if (response.session?.sessionToken) {
          const tokens = {
            accessToken: response.session.sessionToken,
            refreshToken: response.session.refreshToken || "",
            expiresAt: response.expiresAt || new Date(response.session.expires).getTime(),
            refreshExpiresAt: response.refreshExpiresAt, // Store refresh token expiry from server
          };
          setAuthTokens(tokens);

          // Store the selected workspace for future logins for this specific user
          // This ensures user returns to their workspace after signup
          if (response.selectedWorkspace?.id && response.user?.email) {
            const userWorkspaceKey = `currentWorkspaceId_${response.user.email}`
            localStorage.setItem(userWorkspaceKey, response.selectedWorkspace.id)
          }
        }
        setUserData(response.user);
      }

      return response;
    } catch (error) {
      // Show error message to user
      if (error instanceof Error) {
        throw new Error(`Signup failed: ${error.message}`);
      }
      throw new Error("An unexpected error occurred during signup");
    }
  }

  /**
   * Revokes the session server-side, best-effort.
   *
   * Deliberately a bare `fetch` rather than `api.post`: by the time this runs
   * the tokens are already out of storage, so the auth-aware client would find
   * no token, treat the call as unauthenticated, and — worse — a 401 would send
   * it down the refresh-then-hard-redirect path and clobber the sign-out
   * navigation. The Authorization header is passed in from the pre-clear
   * snapshot instead.
   *
   * `keepalive` is what makes the request survive the document unload that
   * `signout` triggers immediately afterwards; without it the browser cancels
   * the in-flight request and the server-side session is never revoked.
   *
   * @param authHeader - Authorization header captured before storage was cleared
   */
  private static async revokeServerSession(authHeader: string | null): Promise<void> {
    // No token means there is no server session to revoke.
    if (!authHeader) return;

    try {
      await fetch(buildUrl("/auth/signout"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: authHeader,
        },
        body: "{}",
        keepalive: true,
      });
    } catch (error) {
      // Best-effort: local credentials are already gone either way.
      logger.warn("Signout request failed", error);
    }
  }

  /**
   * Signs out the current user.
   *
   * Order matters, and it is the opposite of what it used to be. Previously the
   * server round-trip was awaited *before* anything local changed, so the fully
   * rendered app — sidebar, workspace, the user's own data — stayed on screen
   * for as long as the network took, with no indication that anything was
   * happening. Now local credentials are dropped synchronously and the
   * navigation starts in the same tick; the revocation call rides along via
   * `keepalive`.
   *
   * The redirect is a full document load rather than a client-side navigation
   * on purpose: it guarantees a clean JS heap, so no store, cache or React tree
   * can carry the previous user's data into the next session in this tab.
   *
   * @param options.redirect - Pass false to stay on the page (caller navigates)
   * @returns Promise resolving when signout is complete
   */
  static async signout(options?: { redirect?: boolean }): Promise<void> {
    const shouldRedirect = options?.redirect !== false;

    // Snapshot everything the cleanup below needs while storage is still intact.
    const authHeader = getAuthHeader();
    const currentUser = AuthService.getCurrentUser();

    // Clear local authentication data FIRST, synchronously, so that from this
    // point on nothing in the tab can present itself as signed in.
    clearAuthTokens();
    clearUserData();

    // Clear workspace preference on logout for the current user only
    if (currentUser?.email) {
      try {
        localStorage.removeItem(`currentWorkspaceId_${currentUser.email}`);
      } catch (error) {
        logger.warn("Failed to clear workspace preference on signout", error);
      }
    }

    const revoked = AuthService.revokeServerSession(authHeader);

    // Redirect to signin page — skippable for callers that need to route
    // somewhere else themselves right after signing out (e.g. the
    // invitation flow, which decides between /signin and /signup based on
    // whether the invited email already has an account). A hard redirect
    // here would race with and clobber that caller-chosen navigation.
    if (shouldRedirect && typeof window !== "undefined") {
      // `replace`, not `href`: the app page must not stay in session history,
      // or Back walks straight into a bfcache-restored copy of it.
      showSignOutCurtain();
      window.location.replace("/signin");
      return;
    }

    // Callers that stay on this page keep the old contract and await the
    // server round-trip, so whatever they do next sees a revoked session.
    await revoked;
  }

  /**
   * Refreshes the authentication token
   * @param refreshToken - Refresh token
   * @returns Promise resolving to new token data
   */
  static async refreshToken(
    refreshToken: string
  ): Promise<RefreshTokenResponse> {
    const response = (await api.post(
      "/auth/refresh",
      {
        refreshToken,
      },
      {
        requiresAuth: false,
        showErrorToast: false, // Don't show error toast for token refresh
        showSuccessToast: false,
      }
    )) 

    return response;
  }

  /**
   * Requests password reset for an email (sends temporary password)
   * @param email - User email address
   * @returns Promise resolving when request is sent
   */
  static async forgotPassword(
    email: string
  ): Promise<{ success: boolean; message: string; tempPasswordId?: string }> {
    const response = await api.post(
      "/auth/forgot-password",
      { email },
      {
        requiresAuth: false,
        showSuccessToast: false,
        showErrorToast: false,
      }
    );
    return response;
  }

  /**
   * Resets password with temporary password
   * @param email - User email address
   * @param tempPassword - Temporary password from email
   * @param newPassword - New password
   * @returns Promise resolving when password is reset
   */
  static async resetPasswordWithTempPassword(
    email: string,
    tempPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post(
      "/auth/reset-password",
      {
        email,
        tempPassword,
        newPassword,
      },
      {
        requiresAuth: false,
        showSuccessToast: false,
        showErrorToast: false,
      }
    );
    return response;
  }

  /**
   * @deprecated Use resetPasswordWithTempPassword instead
   * Resets password with token (legacy method)
   * @param token - Password reset token
   * @param newPassword - New password
   * @returns Promise resolving when password is reset
   */
  static async resetPassword(
    token: string,
    newPassword: string
  ): Promise<void> {
    await api.post(
      "/auth/reset-password-legacy",
      {
        token,
        password: newPassword,
      },
      {
        requiresAuth: false,
        showSuccessToast: true,
        showErrorToast: true,
      }
    );
  }

  /**
   * Verifies email with verification token
   * @param token - Email verification token
   * @returns Promise resolving when email is verified
   */
  static async verifyEmail(token: string): Promise<void> {
    await api.post(
      "/auth/verify-email",
      { token },
      {
        requiresAuth: false,
        showSuccessToast: true,
        showErrorToast: true,
      }
    );
  }

  /**
   * Changes user password (requires authentication)
   * @param currentPassword - Current password
   * @param newPassword - New password
   * @returns Promise resolving when password is changed
   */
  static async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<void> {
    await api.post(
      "/user/change-password",
      {
        currentPassword,
        newPassword,
      },
      {
        requiresAuth: true,
        showSuccessToast: true,
        showErrorToast: true,
      }
    );
  }

  /**
   * Deletes the current user account
   * @param password - User password for confirmation
   * @returns Promise resolving when account is deleted
   */
  static async deleteAccount(password: string): Promise<void> {
    try {
      await api.delete("/user/delete-account", {
        body: { password },
        requiresAuth: true,
        showSuccessToast: true,
        showErrorToast: true,
      });
    } finally {
      // Clear authentication data after account deletion
      clearAuthTokens();
      clearUserData();

      // Redirect to home page
      if (typeof window !== "undefined") {
        window.location.href = "/";
      }
    }
  }

  /**
   * Verifies OTP code and completes email verification
   * @param data - OTP verification data
   * @returns Promise resolving to verification response
   */
  static async verifyOtp(data: { otpId: string; otp: string }): Promise<{
    success: boolean;
    data?: {
      session?: {
        sessionToken: string;
        refreshToken?: string;
        expires: string;
      };
      user: Record<string, unknown>;
    };
  }> {
    try {
      const response = await api.post("/auth/verify-otp", data, {
        requiresAuth: false,
        showSuccessToast: false,
        showErrorToast: true, // Show error toast for OTP verification failures
      });
      logger.debug("OTP verification response:", response);

      // Store authentication data if verification successful
      if (response.success && response.data?.session?.sessionToken) {
        const tokens = {
          accessToken: response.data.session.sessionToken,
          refreshToken: response.data.session.refreshToken || "",
          expiresAt: response.data.expiresAt || new Date(response.data.session.expires).getTime(),
          refreshExpiresAt: response.data.refreshExpiresAt, // Store refresh token expiry from server
        };

        setAuthTokens(tokens);
        setUserData(response.data.user);

        // Store the selected workspace for future logins for this specific user
        // This ensures user returns to their workspace after email verification
        if (response.data.selectedWorkspace?.id && response.data.user?.email) {
          const userWorkspaceKey = `currentWorkspaceId_${response.data.user.email}`
          localStorage.setItem(userWorkspaceKey, response.data.selectedWorkspace.id)
        }
      }

      return response;
    } catch (error) {
      logger.error("OTP verification failed", error);
      // Deliberately does NOT clear stored credentials. A failed verification
      // attempt only means THIS request failed; it says nothing about a
      // session already in storage. Clearing here signed users out of the
      // session a previous, successful call had just established — the code
      // can only be redeemed once, so any second attempt on the same OTP comes
      // back 400 "already used" and used to wipe the tokens, stranding the
      // brand-new account on /workspace/create with "Not authenticated".
      // Tokens are only ever written on success, so there is nothing partial
      // to roll back here either.

      if (error instanceof Error) {
        throw new Error(`OTP verification failed: ${error.message}`);
      }
      throw new Error("An unexpected error occurred during OTP verification");
    }
  }

  /**
   * Resends OTP verification code
   * @param data - Resend OTP data
   * @returns Promise resolving to resend response
   */
  static async resendOtp(data: {
    otpId: string;
  }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post("/auth/resend-otp", data, {
        requiresAuth: false,
        showSuccessToast: false,
        showErrorToast: true, // Show error toast for OTP resend failures
      });

      return response;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to resend OTP: ${error.message}`);
      }
      throw new Error("An unexpected error occurred while resending OTP");
    }
  }

  /**
   * Gets current user data from storage
   * @returns Current user data or null if not found
   */
  static getCurrentUser(): any | null {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  }
}

/**
 * Convenience methods for common auth operations
 */
export const authService = {
  signin: AuthService.signin,
  signup: AuthService.signup,
  signout: AuthService.signout,
  refreshToken: AuthService.refreshToken,
  forgotPassword: AuthService.forgotPassword,
  resetPassword: AuthService.resetPassword,
  resetPasswordWithTempPassword: AuthService.resetPasswordWithTempPassword,
  verifyEmail: AuthService.verifyEmail,
  changePassword: AuthService.changePassword,
  deleteAccount: AuthService.deleteAccount,
  verifyOtp: AuthService.verifyOtp,
  resendOtp: AuthService.resendOtp,
  getCurrentUser: AuthService.getCurrentUser,
};

/**
 * Auth service with additional utilities
 */
export const auth = {
  ...authService,

  /**
   * Checks if user is currently signed in
   */
  isSignedIn(): boolean {
    const userData = clearUserData;
    return !!userData;
  },


  /**
   * Signs out and redirects to specific URL
   */
  async signOutAndRedirect(redirectUrl: string = "/signin"): Promise<void> {
    await authService.signout();
    if (typeof window !== "undefined") {
      window.location.href = redirectUrl;
    }
  },
};
